<template>
  <div>
    <PageHeader>
      <template #header-title>{{ $t('Your placements in Worksome') }}</template>
    </PageHeader>
    <div class="card card-white">
      <div v-if="placementsLoadingError" class="tw-mt-9">
        <ContentLoadingErrorNotice />
      </div>
      <template v-else>
        <RecruiterPlacementsFilters @update-filters="updateFilters" />
        <BaseDataTable
          :entries="entries"
          :columns="columns"
          :loading="loading"
          :paginator-info="paginatorInfo"
          class="recruiter-placements-table"
          :default-page-length="10"
          tall-rows
          @search="search"
        >
          <template #StartDate="{ row }">
            {{ row.startDate ? moment(row.startDate)?.format('YYYY-MM-DD') : '--' }}
          </template>

          <template #Company="{ row }">
            <div class="tw-max-w-[350px]">
              <WdsAvatarLabel
                sm
                :path="getAvatarPath(row.company?.avatar, row.company?.id)"
                :text="row.company?.name"
                truncated
                :text-link="
                  laravelNamedRoute(namedRoutes.frontend.user.profile.view, {
                    profile: row.company?.profile?.id,
                  })
                "
              />
            </div>
          </template>

          <template #Job="{ row }">
            <div class="tw-max-w-[200px]">
              <a
                v-if="row.job?.id && row.job?.name"
                :href="
                  laravelNamedRoute(namedRoutes.jobs.show, {
                    job: row.job.id,
                  })
                "
              >
                <div class="tw-truncate tw-max-w-[200px]">{{ row.job?.name }}</div>
              </a>
              <div v-else>{{ $t('Job missing') }}</div>

              <div>{{ row.job?.region }}</div>
            </div>
          </template>

          <template #Worker="{ row }">
            <a
              v-if="row.worker?.profile"
              class="tw-truncate tw-max-w-[200px]"
              :href="
                laravelNamedRoute(namedRoutes.frontend.user.profile.view, {
                  profile: row.worker.profile.id,
                })
              "
            >
              {{ row.worker?.name }}
            </a>
            <div v-else class="tw-truncate tw-max-w-[200px]">{{ row.worker?.name }}</div>
            <div class="tw-truncate tw-max-w-[200px]">{{ row.worker?.jobTitle }}</div>
          </template>

          <template #Rate="{ row }">
            <div>{{ row.currency }} {{ row.rate }}</div>
            <div class="tw-capitalize">{{ row.rateType }}</div>
          </template>

          <template #Ownership="{ row }">
            <WdsLabel v-if="row.recruiterOwnershipIsExpired" error sm>{{ $t('Expired') }}</WdsLabel>
            <WdsTooltip
              v-else
              :text="$tc('Expires in :count days', row.recruiterOwnershipDaysLeft)"
            >
              <WdsLabel success sm>{{ $t('Active') }}</WdsLabel>
            </WdsTooltip>
          </template>

          <template #Terms="{ row }">
            <div>{{ row.recruiterFee }}%</div>
            <div>{{ row.recruiterOwnershipDays }} {{ $t('days') }}</div>
          </template>

          <template #Contract="{ row }">
            <div v-if="row.latestContract && row.latestContract.status === 'ACTIVE'">
              <WdsTooltip global>
                <WdsLabel sm>#{{ row.number }}</WdsLabel>
                <template #tooltip-content>
                  <strong>{{ $t('Contract timeframe') }}:</strong>
                  <br />
                  {{ row.startDate ? moment(row.startDate)?.format('YYYY-MM-DD') : '?' }} -
                  {{ row.endDate ? moment(row.endDate)?.format('YYYY-MM-DD') : '?' }}
                  <br />
                  <br />
                  <strong>{{ $t('Ownership start date') }}:</strong>
                  {{ moment(row.recruiterOwnershipStartDate)?.format('YYYY-MM-DD') }}
                </template>
              </WdsTooltip>
            </div>
            <div v-else-if="row.recruiterManagesWorkers">
              <WdsButtonText @click="onContractButtonClick(row.id, 'accept')">
                Accept Contract
              </WdsButtonText>
            </div>
          </template>

          <template #Action="{ row }">
            <RecruiterActionMenu
              :hire-id="row.id"
              :recruiter-id="recruiterAccountId"
              :manages-workers="row.recruiterManagesWorkers"
              :contract="row.latestContract"
              :conversation="row.conversation"
              @show-contract-button-clicked="onContractButtonClick(row.id, 'show')"
              @hire-canceled="refetch"
            />
          </template>
        </BaseDataTable>
      </template>
    </div>

    <Contract
      v-if="hireId && display"
      v-model:loading="contractLoading"
      :hire-id="hireId"
      :action="action"
      @close="display = false"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'
import moment from 'moment'
import { WdsLabel, WdsAvatarLabel, WdsTooltip, WdsButtonText } from '@worksome/wds-components'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import BaseDataTable from '@/components/BaseDataTable'
import Contract from '@/components/Contract'
import { stringToEnum } from '@/utils/tools'
import { getAvatarPath } from '@/utils/avatar'
import RecruiterPlacementsFilters from '@/components/RecruiterPlacements/RecruiterPlacementsFilters.vue'
import RecruiterActionMenu from '@/components/RecruiterPlacements/RecruiterActionMenu.vue'
import useQueryEncoder from '@/composables/useQueryEncoder'
import PageHeader from '@/components/PageHeader.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps({
  recruiterAccountId: {
    type: String,
    required: true,
  },
  status: {
    type: Array,
    default: () => ['ACTIVE', 'COMPLETED'],
  },
})

const placementsLoadingError = ref(false)

const queryVariables = ref({
  page: 1,
  perPage: 10,
  search: '',
  accounts: [props.recruiterAccountId],
  status: props.status,
  orderBy: [
    {
      field: 'START_DATE',
      order: 'DESC',
    },
  ],
  recruiterOwnershipIsExpired: null,
  startDateRange: null,
  companies: [],
  workers: [],
  contractStatus: [],
})

const {
  result,
  loading,
  refetch,
  onError: onPlacementsLoadingError,
} = useQuery(
  gql`
    query hires(
      $page: Int!
      $perPage: Int!
      $search: String
      $accounts: [ID!]
      $status: [HireStatus!]
      $orderBy: [HiresOrderByClauseInput!]
      $recruiterOwnershipIsExpired: Boolean
      $startDateRange: DateRangeInput
      $companies: [ID!]
      $workers: [ID!]
      $contractStatus: [ContractStatus!]
    ) {
      hires(
        page: $page
        first: $perPage
        search: $search
        accounts: $accounts
        status: $status
        orderBy: $orderBy
        recruiterOwnershipIsExpired: $recruiterOwnershipIsExpired
        startDateRange: $startDateRange
        companies: $companies
        workers: $workers
        contractStatus: $contractStatus
      ) {
        paginatorInfo {
          ...PaginatorInfoFragment
        }
        data {
          id
          number
          startDate
          endDate
          recruiterFee
          recruiterOwnershipDays
          recruiterOwnershipStartDate
          recruiterOwnershipIsExpired
          recruiterOwnershipDaysLeft
          recruiterManagesWorkers
          currency
          rate
          rateType
          conversation {
            url
          }
          latestContract {
            id
            status
            pdfUrl
          }
          job {
            id
            name
            region
          }
          worker {
            name
            profile {
              id
            }
            jobTitle
          }
          company {
            id
            name
            avatar
            profile {
              id
            }
            settings {
              staffingAgencyManagesWorker
            }
          }
        }
      }
    }
    ${BaseDataTable.fragments.pagination}
  `,
  queryVariables,
  {
    clientId: 'publicApi',
  },
)

onPlacementsLoadingError((error) => {
  placementsLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})

const entries = computed(() => result.value?.hires?.data)

const paginatorInfo = computed(() => result.value?.hires?.paginatorInfo)

// Since the columns on the UI do not map 1-1 with the value being sorted, use this mapping when sorting
const columnSortMapping = {
  startDate: 'START_DATE',
  company: 'COMPANY_NAME',
  job: 'JOB_NAME',
  worker: 'WORKER_NAME',
  contract: 'NUMBER',
}

const search = (values) => {
  const orderBy = []
  for (const key in values.order) {
    if (values.order[key]) {
      orderBy.push({
        field: columnSortMapping[key],
        order: stringToEnum(values.order[key]),
      })
    }
  }

  queryVariables.value = {
    ...queryVariables.value,
    page: values.page + 1,
    perPage: values.perPage,
    search: values.search,
    orderBy,
  }
}

const updateFilters = (filters) => {
  queryVariables.value = {
    ...queryVariables.value,
    ...filters,
  }
}

const columns = [
  {
    key: 'startDate',
    title: $t('Start date'),
    order: 'desc',
    slot: 'StartDate',
    orderAble: true,
  },
  {
    key: 'company',
    slot: 'Company',
    title: $t('Company'),
    orderAble: true,
  },
  {
    key: 'job',
    title: $t('Job'),
    slot: 'Job',
    orderAble: true,
  },
  {
    key: 'worker',
    title: $t('Worker'),
    slot: 'Worker',
    orderAble: true,
  },
  {
    key: 'rate',
    title: $t('Rate'),
    slot: 'Rate',
    orderAble: false,
  },
  {
    key: 'terms',
    title: $t('Terms'),
    slot: 'Terms',
    orderAble: false,
  },
  {
    key: 'ownership',
    title: $t('Ownership'),
    slot: 'Ownership',
    orderAble: false,
  },
  {
    key: 'contract',
    title: $t('Contract'),
    slot: 'Contract',
    orderAble: true,
  },
  {
    slot: 'Action',
    key: 'action',
    title: '',
    class: 'vmiddle',
  },
]

const display = ref(false)
const hireId = ref(null)
const contractLoading = ref(false)
const action = ref('show')

const acceptId = useQueryEncoder('acceptId')

const onContractButtonClick = (id, type) => {
  hireId.value = id
  display.value = true
  action.value = type
}

onMounted(() => {
  if (acceptId.value) {
    onContractButtonClick(acceptId.value, 'accept')
  }
})
</script>

<style scoped>
.recruiter-placements-table {
  overflow-x: visible;
}
</style>
